import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import CustomIconButton from './CustomIconButton';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import CustomDropDownInput from './customDropDownInput';
import InputField from './InputField'; // <-- Make sure this is correctly imported

export interface FilterField {
  key: string;
  label: string;
  options?: { value: string; label: string }[];
  defaultValue?: string;
  input?: boolean; // <-- New field to toggle between input and dropdown
}

export interface FilterValues {
  [key: string]: string;
}

export interface CustomFilterModalProps {
  open: boolean;
  onClose: () => void;
  onReset?: () => void;
  title?: string;
  subTitle?: string;
  description?: string;
  filterFields: FilterField[];
  onApply: (data: FilterValues) => void;
  initialValues?: FilterValues;
}

const CustomFilterModal: React.FC<CustomFilterModalProps> = ({
  open,
  onClose,
  onReset,
  title = 'Customise Table',
  subTitle = '',
  description = 'Choose your filter options.',
  filterFields,
  onApply,
  initialValues,
}) => {
  const computeInitialValues = (): FilterValues => {
    const base: FilterValues = filterFields.reduce((acc, field) => {
      acc[field.key] = field.defaultValue || '';
      return acc;
    }, {} as FilterValues);
    return { ...base, ...(initialValues || {}) };
  };

  const [filterValues, setFilterValues] = useState<FilterValues>(computeInitialValues());

  const [expandedState, setExpandedState] = useState<{ [key: string]: boolean }>(
    filterFields.reduce((acc, field) => {
      acc[field.key] = false;
      return acc;
    }, {} as { [key: string]: boolean })
  );

  useEffect(() => {
    if (open) {
      setFilterValues(computeInitialValues());
      setExpandedState(
        filterFields.reduce((acc, field) => {
          acc[field.key] = false;
          return acc;
        }, {} as { [key: string]: boolean })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues, filterFields]);

  const toggleExpanded = (key: string) => {
    setExpandedState((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (fieldKey: string, newValue: string) => {
    setFilterValues((prev) => ({ ...prev, [fieldKey]: newValue }));
  };

  // In CustomFilterModal.tsx - Update the handleReset function
  const handleReset = () => {
    const resetValues: FilterValues = filterFields.reduce((acc, field) => {
      acc[field.key] = field.defaultValue || ''; // Use defaultValue if provided
      return acc;
    }, {} as FilterValues);
    setFilterValues(resetValues);
    if (onReset) onReset();
  };

  const handleApply = () => {
    onApply(filterValues);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          height: '80vh',
        }}
      >
        <div className="flex flex-col h-full justify-between">
          <div className="flex flex-col gap-8">
            {/* Header */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <Typography variant="h6">
                  {title} {subTitle}
                </Typography>
                {onReset && (
                  <CustomIconButton
                    text="Clear All"
                    background="transparent"
                    variant="outlined"
                    onClick={handleReset}
                  />
                )}
              </div>
              {description && (
                <Typography variant="button" fontWeight={400} color="secondary">
                  {description}
                </Typography>
              )}
            </div>

            {/* Filter fields */}
            {filterFields.map((field) => (
              <div key={field.key} className="flex flex-col gap-2">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleExpanded(field.key)}
                >
                  <Typography variant="body1">{field.label}</Typography>
                  <img
                    src={expandedState[field.key] ? arrowUp : arrowDown}
                    width={24}
                    height={24}
                    alt={expandedState[field.key] ? 'collapse' : 'expand'}
                  />
                </div>
                {expandedState[field.key] && (
                  <div className="max-w-[50%]">
                    {field.input ? (
                      <InputField
                        type="description"
                        name={field.key}
                        value={filterValues[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        placeholder={`Enter ${field.label}`}
                      />
                    ) : (
                      <CustomDropDownInput
                        options={field.options || []}
                        value={filterValues[field.key] || ''}
                        onChange={(val) => handleChange(field.key, val)}
                        placeholder={`Select ${field.label}`}
                      />
                    )}
                  </div>
                )}
                <hr />
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 items-center justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline
              variant="outlined"
              onClick={onClose}
            />
            <CustomIconButton
              text="Apply"
              variant="contained"
              onClick={handleApply}
            />

          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default CustomFilterModal;
